import type { ReactNode } from "react";
import { useTranslations } from "use-intl";

import {
  CheckIcon,
  ChevronRightIcon,
  DateIcon,
  WishlistFilledIcon,
  GlobeIcon,
  MusicIcon,
} from "@/components/appIcons";
import { CountryFlag } from "@/components/country-flag";
import { DeferredReleaseCover } from "@/components/deferred-release-cover";
import type { ReleaseCardData } from "@/components/release-card";
import type { ReleaseCardEditionIdentity } from "@/components/release-card-edition-identity";
import { ReleaseCardImageStack } from "@/components/release-card-image-stack";
import type { ReleaseCardModel } from "@/components/release-card-model";
import { releaseCardSavedState } from "@/components/release-card-saved-state";
import { ReleaseFormatFeatureTags } from "@/components/release-format-feature-tags";
import { useFormatDate } from "@/hooks/use-format-date";
import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { cn } from "@/lib/utils";

interface BodyProps {
  catalogNumberId?: string;
  headingId?: string;
  inCollection: boolean;
  inWishlist: boolean;
  labelId?: string;
  model: ReleaseCardModel;
  release: ReleaseCardData;
  trailing?: ReactNode;
  variant: "full" | "compact" | "showcase" | "index";
}

function ReleaseCover({ inCollection, inWishlist, model, release, variant }: BodyProps) {
  if (model.showcase) {
    return (
      <ReleaseCardImageStack
        className={cn(
          variant === "index" &&
            "size-28 shrink-0 rounded-md md:size-auto md:w-full md:rounded-none [&>div]:inset-2 md:[&>div]:inset-5",
        )}
        imageCount={release.imageCount}
        releaseId={release.id}
        title={release.title}
      />
    );
  }
  const saved = inCollection || inWishlist;
  return (
    <div
      className={cn(
        model.coverSize,
        "relative aspect-square rounded-md overflow-hidden bg-background",
      )}
    >
      <DeferredReleaseCover
        imageCount={release.imageCount}
        releaseId={release.id}
        showLoadingSpinner
        sizes={variant === "compact" ? "56px" : "(min-width: 768px) 112px, 96px"}
        title={release.title}
      />
      {saved && (
        <div
          className={cn(
            "absolute top-1 right-1 z-10 size-5 rounded-full text-white flex items-center justify-center shadow-md",
            inCollection ? "bg-green-500" : "bg-pink-500",
          )}
        >
          {inCollection ? (
            <CheckIcon className="text-xs" />
          ) : (
            <WishlistFilledIcon className="text-xs" />
          )}
        </div>
      )}
    </div>
  );
}

function ReleaseHeadingTrailing({
  showcase,
  trailing,
}: {
  showcase: boolean;
  trailing?: ReactNode;
}) {
  if (trailing) return <div className="shrink-0">{trailing}</div>;
  if (showcase) return null;
  return <ChevronRightIcon className="text-muted-foreground group-hover:text-primary shrink-0" />;
}

function ReleaseEditionYear({ edition }: { edition: ReleaseCardEditionIdentity }) {
  if (!edition.year) return null;
  return (
    <>
      <time dateTime={String(edition.year)}>{edition.year}</time>
      {(edition.country || edition.format) && <span aria-hidden="true">·</span>}
    </>
  );
}

function ReleaseEditionCountry({
  countryCode,
  edition,
}: {
  countryCode: string | null;
  edition: ReleaseCardEditionIdentity;
}) {
  if (!edition.country) return null;
  return (
    <>
      <span className="inline-flex items-center gap-1">
        {countryCode && <CountryFlag code={countryCode} decorative fallback={null} />}
        {edition.country}
      </span>
      {edition.format && <span aria-hidden="true">·</span>}
    </>
  );
}

function ReleaseEditionFormat({ edition }: { edition: ReleaseCardEditionIdentity }) {
  return (
    <>
      {edition.format && <span>{edition.format}</span>}
      {edition.fallback && <span>{edition.fallback}</span>}
    </>
  );
}

function ReleaseIndexHeading({
  headingId,
  model,
  release,
}: Pick<BodyProps, "headingId" | "model" | "release">) {
  const edition = model.editionIdentity;
  const includeTitleContext = edition.text !== release.title;

  return (
    <h3
      id={headingId}
      className={cn(
        model.titleClass,
        "flex flex-wrap items-center gap-x-1 group-hover:text-primary group-focus-within:text-primary",
      )}
    >
      {includeTitleContext && <span className="sr-only">{release.title}: </span>}
      <ReleaseEditionYear edition={edition} />
      <ReleaseEditionCountry countryCode={release.country} edition={edition} />
      <ReleaseEditionFormat edition={edition} />
    </h3>
  );
}

function ReleaseStandardMetadata({ model, release }: Pick<BodyProps, "model" | "release">) {
  const formatDate = useFormatDate();

  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
      {release.date && (
        <span className="flex items-center gap-1 whitespace-nowrap">
          {!model.showcase && <DateIcon className="text-xs" />}
          {formatDate(release.date)}
        </span>
      )}
      {release.date && release.country && <span aria-hidden="true">·</span>}
      {release.country && (
        <span
          className="flex items-center gap-1.5 whitespace-nowrap"
          title={model.countryName ?? release.country}
        >
          <CountryFlag
            code={release.country}
            decorative
            fallback={model.showcase ? null : <GlobeIcon className="text-xs" />}
          />
          <span>{model.countryName}</span>
        </span>
      )}
    </div>
  );
}

function ReleaseHeading({
  headingId,
  labelId,
  model,
  release,
  trailing,
}: Pick<BodyProps, "headingId" | "labelId" | "model" | "release" | "trailing">) {
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        {model.index ? (
          <ReleaseIndexHeading headingId={headingId} model={model} release={release} />
        ) : (
          <p className={cn(model.titleClass, "group-hover:text-primary")}>{release.title}</p>
        )}
        <ReleaseHeadingTrailing showcase={model.showcase} trailing={trailing} />
      </div>
      {release.labelNames.length > 0 && (
        <p id={labelId} className="text-sm text-muted-foreground truncate">
          {release.labelNames.join(" / ")}
        </p>
      )}
      {!model.index && <ReleaseStandardMetadata model={model} release={release} />}
    </>
  );
}

function ReleaseMediaSummaryRow({ model, release }: Pick<BodyProps, "model" | "release">) {
  const t = useTranslations("album");

  if (!model.hasMediaSummaryRow) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      {!model.showcase && <MusicIcon className="text-xs" />}
      {model.formatLabel && <span>{model.formatLabel}</span>}
      {model.formatLabel && model.hasTrackSummary && <span>·</span>}
      {model.hasTrackSummary && <span>{t("tracks", { count: release.trackCount })}</span>}
      {model.showFormatText && (
        <span
          className={cn(
            "inline-flex items-center rounded bg-secondary px-1.5 py-0.5 font-medium text-foreground",
            model.index ? "text-xs" : "text-[10px]",
          )}
        >
          {release.formatText}
        </span>
      )}
    </div>
  );
}

function ReleaseFormatMetadata({ model, release }: Pick<BodyProps, "model" | "release">) {
  return (
    <>
      <ReleaseMediaSummaryRow model={model} release={release} />
      {release.mediaFeatures.length > 0 && !model.hasFormatPayload && (
        <ReleaseFormatFeatureTags
          mediaFeatures={release.mediaFeatures}
          tagClassName={model.index ? "text-xs" : "text-[10px]"}
        />
      )}
    </>
  );
}

function ReleaseSavedStateChip({
  inCollection,
  inWishlist,
}: Pick<BodyProps, "inCollection" | "inWishlist">) {
  const t = useTranslations("globals.lists");
  const state = releaseCardSavedState(inCollection, inWishlist);

  if (!state) return null;

  const collection = state === "collection";
  return (
    <span
      className={cn(
        "inline-flex w-fit items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-semibold",
        collection
          ? "border-green-600/30 bg-green-600/15 text-green-500"
          : "border-pink-600/30 bg-pink-600/15 text-pink-500",
      )}
    >
      {collection ? <CheckIcon className="text-sm" /> : <WishlistFilledIcon className="text-sm" />}
      {t(collection ? "inCollection" : "inWishlist")}
    </span>
  );
}

function ReleaseSignalTags({ model }: Pick<BodyProps, "model">) {
  const tr = useTranslations("release");
  const tFeature = useTranslations("shared.mediaFeatures");
  const policyLabel = (tag: string) => {
    const featureKey = tag as Parameters<typeof tFeature>[0];
    if (tFeature.has(featureKey)) return tFeature(featureKey);
    const releaseKey = tag as Parameters<typeof tr>[0];
    return tr.has(releaseKey) ? tr(releaseKey) : formatCatalogTaxonomyLabel(tag);
  };
  return (
    <>
      {model.detailTags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {model.detailTags.map((tag) => (
            <span
              key={tag}
              className={cn(
                "inline-flex max-w-full items-center truncate rounded border border-border/60 bg-secondary px-1.5 py-0.5 font-medium text-muted-foreground",
                model.index ? "text-xs" : "text-[10px]",
              )}
              title={tag}
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      {model.policyTags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {model.policyTags.map((tag) => (
            <span
              key={tag}
              className={cn(
                "inline-flex items-center rounded bg-amber-500/15 px-1.5 py-0.5 font-medium text-amber-400",
                model.index ? "text-xs" : "text-[10px]",
              )}
            >
              {policyLabel(tag)}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

function ReleaseCatalogNumber({
  catalogNumberId,
  model,
  release,
}: Pick<BodyProps, "catalogNumberId" | "model" | "release">) {
  const t = useTranslations("album");

  if (release.catnos.length === 0) return null;

  return (
    <div
      className={cn(
        "mt-auto flex flex-wrap gap-x-3 gap-y-0.5 pt-1 text-muted-foreground",
        model.index ? "text-xs" : "text-[11px]",
      )}
    >
      <p id={catalogNumberId}>{t("catalogNumber", { number: release.catnos.join(" · ") })}</p>
    </div>
  );
}

function ReleaseStatus({ model }: Pick<BodyProps, "model">) {
  const tr = useTranslations("release");

  if (!model.visibleStatus) return null;

  return (
    <span className="mt-1 inline-block self-start rounded bg-amber-500/20 px-2 py-0.5 text-xs font-medium text-amber-500">
      {tr.has(model.visibleStatus) ? tr(model.visibleStatus) : model.visibleStatus}
    </span>
  );
}

function ReleaseNotes({ model, release }: Pick<BodyProps, "model" | "release">) {
  if (!model.showNotes) return null;

  return (
    <div className={cn("text-xs text-muted-foreground", model.index && "hidden md:block")}>
      <span className="line-clamp-1 italic">“{release.notes}”</span>
    </div>
  );
}

function ReleaseTags(props: Pick<BodyProps, "catalogNumberId" | "model" | "release">) {
  if (props.model.index) {
    return (
      <>
        <ReleaseCatalogNumber {...props} />
        <ReleaseSignalTags {...props} />
        <ReleaseStatus model={props.model} />
        <ReleaseNotes model={props.model} release={props.release} />
      </>
    );
  }

  return (
    <>
      <ReleaseSignalTags {...props} />
      <ReleaseNotes model={props.model} release={props.release} />
      <ReleaseCatalogNumber {...props} />
      <ReleaseStatus model={props.model} />
    </>
  );
}

export function ReleaseCardBody(props: BodyProps) {
  return (
    <>
      <ReleaseCover {...props} />
      <div
        className={cn(
          "flex-1 min-w-0 flex flex-col gap-1",
          props.model.showcase && "gap-0.5 p-3",
          props.model.index && "p-0 md:p-3",
        )}
      >
        <ReleaseHeading
          headingId={props.headingId}
          labelId={props.labelId}
          model={props.model}
          release={props.release}
          trailing={props.trailing}
        />
        {props.model.index && (
          <ReleaseSavedStateChip inCollection={props.inCollection} inWishlist={props.inWishlist} />
        )}
        <ReleaseFormatMetadata model={props.model} release={props.release} />
        <ReleaseTags
          catalogNumberId={props.catalogNumberId}
          model={props.model}
          release={props.release}
        />
      </div>
    </>
  );
}
